// The wire shape of one grant, stated once (`resource.ts` says why here).
//
// Two doors project this body and they were BYTE-IDENTICAL: `toGrantDto`
// (`apps/api/src/routes/admin.ts`) and `maskGrant` (`packages/mcp/src/army/grant.ts`)
// held the same twelve keys in the same order, and nothing mechanical kept them
// that way — an operator reading `grant list` over MCP and the same history in
// the admin radar are reading one control object, so a field added to one copy
// and not the other is a radar that disagrees with the tool about who holds free
// capacity.

import { z } from 'zod';
import { GrantAmountSchema } from '../entities/grant';
import { defineResource, type WireOf } from './resource';

/**
 * What the projection reads off a grant.
 *
 * STRUCTURAL, and deliberately not `Grant` (`entities/grant.ts`): the row a door
 * actually holds is `GrantView` (`packages/mcp/src/army/grant.ts`), which carries
 * a port-resolved `active` the stored entity has no column for. Stating the
 * minimum both satisfy is what lets one resource serve both without `domain`
 * importing either.
 */
export type GrantEntity = {
  id: string;
  /** `account` (an `accounts.id`) | `user` (a `users.id`). */
  subjectKind: string;
  subjectId: string;
  /** A `GrantKind` (`entities/grant.ts` — the enum is the list, not this line). */
  kind: string;
  /** Null for the flag kinds (a flag has no "how much"); a positive integer otherwise. */
  amount: number | null;
  /** `operator` (the admin surface) | `sponsor` (the donations lane). */
  source: string;
  grantedByUserId: string;
  reason: string;
  /** Resolved at the port against ONE frozen instant, never re-derived by a client. */
  active: boolean;
  createdAt: Date;
  expiresAt: Date | null;
  revokedAt: Date | null;
  extendsToOwnedOrgs: boolean;
};

/**
 * The published body. Snake_case, ISO instants, transform-free.
 *
 * `subject_kind`, `kind` and `source` are `z.string()` rather than the domain
 * enums beside them (`GrantSubjectKindSchema`, `GrantKindSchema`,
 * `GrantSourceSchema`) because {@link GrantEntity} — and `GrantView`, the type
 * both doors hold — types them as `string`. Narrowing the schema without
 * narrowing the entity would only make `serialize` stop compiling; narrowing
 * both is a port change across `deps/grants.ts`, not a wire extraction.
 *
 * `reason` is `z.string()` and NOT `GrantReasonSchema`: that one carries
 * `.trim()`, which is an input rule (and a transform this schema may not hold).
 * What a body publishes is what the row already contains.
 */
export const GrantWireSchema = z.object({
  id: z.uuid(),
  subject_kind: z.string(),
  subject_id: z.string(),
  kind: z.string(),
  amount: GrantAmountSchema.nullable(),
  source: z.string(),
  granted_by_user_id: z.string().min(1),
  reason: z.string(),
  active: z.boolean(),
  created_iso: z.iso.datetime(),
  /** When it stops counting on its own; null = open-ended. */
  expires_iso: z.iso.datetime().nullable(),
  /** When an operator pulled it early; null = never. */
  revoked_iso: z.iso.datetime().nullable(),
  extends_to_owned_orgs: z.boolean(),
});

/** The one grant projection. Every door calls this rather than re-deriving it. */
export const GrantResource = defineResource<GrantEntity, typeof GrantWireSchema>({
  schema: GrantWireSchema,
  serialize: (g) => ({
    id: g.id,
    subject_kind: g.subjectKind,
    subject_id: g.subjectId,
    kind: g.kind,
    amount: g.amount,
    source: g.source,
    granted_by_user_id: g.grantedByUserId,
    reason: g.reason,
    active: g.active,
    created_iso: g.createdAt.toISOString(),
    expires_iso: g.expiresAt?.toISOString() ?? null,
    revoked_iso: g.revokedAt?.toISOString() ?? null,
    extends_to_owned_orgs: g.extendsToOwnedOrgs,
  }),
});

/** What a client should type a grant body against. */
export type GrantWire = WireOf<typeof GrantResource>;

/** What the revoke projection reads: the grant as it now stands, plus the CAS verdict. */
export type GrantRevocationEntity = { grant: GrantEntity; revoked: boolean };

/**
 * `DELETE /v1/admin/grants/:id` — the grant, flattened, plus whether THIS call
 * is the one that stopped it.
 *
 * `revoked: false` is a benign no-op (the row was already stopped and keeps its
 * FIRST `revoked_iso`), never an error and never a 404, so the body has to
 * carry the verdict rather than let a 200 stand for both outcomes.
 *
 * A resource rather than a spread at the call site for the reason
 * `apps/api/src/openapi.ts` states about itself: a route's `response: { kind:
 * 'entity' }` publishes a body only when it IS a resource exactly, and that file named this operation
 * as one of the two it could not publish "until the addition is itself a
 * resource".
 *
 * The MCP `grant` verb's `revoke` is deliberately NOT this shape — it answers
 * `{grant: …, revoked: …}`, nested, because an MCP structured result is a named
 * object rather than the entity itself. Same two facts, two envelopes; the
 * entity body inside them is `GrantResource` on both doors, which is the
 * property `contract-body-parity.test.ts` checks.
 */
export const GrantRevocationWireSchema = GrantWireSchema.extend({
  /** True when THIS call stopped it; false when it was already revoked. */
  revoked: z.boolean(),
});

/** What a client should type the revoke body against. */
export type GrantRevocationWire = z.infer<typeof GrantRevocationWireSchema>;

export const GrantRevocationResource = defineResource<
  GrantRevocationEntity,
  typeof GrantRevocationWireSchema
>({
  schema: GrantRevocationWireSchema,
  serialize: (r) => ({ ...GrantResource.serialize(r.grant), revoked: r.revoked }),
});
